/**
 * Tooltip Component
 * Displays contextual information on hover or click
 * Based on WidgetWeb-V2 tooltip component
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import './tooltip.scss';

export enum TooltipTriggerType {
    CLICK = 'click',
    HOVER = 'hover',
}

export enum TooltipDirection {
    UP = 'up',
    DOWN = 'down',
    DOWN_LEFT = 'down-left',
    TOP_RIGHT = 'top-right',
    TOP_LEFT = 'top-left',
    RIGHT_BOTTOM = 'right-bottom',
    RIGHT_MIDDLE = 'right-middle',
    RIGHT_TOP = 'right-top',
}

export interface TooltipProps {
    content: string;
    trigger?: TooltipTriggerType;
    direction?: TooltipDirection;
    className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    trigger = TooltipTriggerType.HOVER,
    direction = TooltipDirection.UP,
    className = '',
}) => {
    const [isShowing, setIsShowing] = useState(false);
    const tooltipRef = useRef<HTMLSpanElement>(null);
    const tooltipId = useRef(`tooltip-${Math.random().toString(36).substring(2, 11)}`);

    const isHoverTrigger = trigger === TooltipTriggerType.HOVER;

    const showTooltip = useCallback(() => setIsShowing(true), []);
    const hideTooltip = useCallback(() => setIsShowing(false), []);
    const toggleTooltip = useCallback(() => setIsShowing((prev) => !prev), []);

    const handleClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
        e.stopPropagation();
        e.preventDefault();

        if (trigger === TooltipTriggerType.CLICK) {
            toggleTooltip();
        }
    };

    // Handle keyboard events
    const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>): void => {
        if (e.key === 'Escape') {
            hideTooltip();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleTooltip();
        }
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        if (!isShowing || isHoverTrigger) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
                hideTooltip();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isShowing, isHoverTrigger, hideTooltip]);

    const classes = ['tooltip', isShowing && 'tooltip--is-showing', `tooltip--${direction}`, className]
        .filter(Boolean)
        .join(' ');

    return (
        <span
            ref={tooltipRef}
            className={classes}
            data-testid="tooltip"
            onClick={handleClick}
            onBlur={hideTooltip}
            onFocus={isHoverTrigger ? showTooltip : undefined}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-describedby={isShowing ? tooltipId.current : undefined}
        >
            <span
                className="tooltip__trigger"
                data-testid="tooltip-trigger"
                onMouseEnter={isHoverTrigger ? showTooltip : undefined}
                onMouseLeave={isHoverTrigger ? hideTooltip : undefined}
            >
                <svg
                    className="tooltip__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="8" cy="8" r="7.5" stroke="currentColor" />
                    <path d="M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="8" cy="5" r="0.75" fill="currentColor" />
                </svg>
            </span>
            <span
                id={tooltipId.current}
                className="tooltip__content"
                role="tooltip"
                aria-hidden={!isShowing}
                data-testid="tooltip-content"
            >
                {content}
            </span>
        </span>
    );
};

export default Tooltip;
